/**
 * Lunchly's intentionally simple product shell.
 *
 * A supervisor gets one decision on this page: which rota to upload. Parsing,
 * generation and persistence happen behind that action, and the finished lunch
 * plan appears immediately below it. The deeper scheduling engine remains fully
 * featured, but its controls do not compete with the everyday workflow.
 */

import { DAY_KEYS, DAY_LABELS, formatDayHeader, formatMinutes, formatWeekCommencing } from '../core/time.js';
import { isRotaEmpty } from '../core/model.js';
import { applyConfirmedRotaCorrections } from '../data/defaults.js';
import {
  copyToClipboard,
  formatLunchRotaDay,
  formatLunchRotaDayHtml,
  formatLunchRotaWeek,
  formatLunchRotaWeekHtml,
} from '../io/exporter.js';
import { detectWeekCommencing, extractRotaFromPdf } from '../io/pdfImport.js';
import { parsePastedRota } from '../io/rotaParser.js';
import { el, on, replaceChildren } from './dom.js';
import { createHeroBackdrop } from './components/heroBackdrop.js';
import { icon } from './icons.js';
import { restartAnimationClass, setupScrollReveals } from './motion.js';
import {
  allWarnings,
  currentRota,
  currentSchedule,
  displayName,
  hasRota,
  weekShifts,
} from './selectors.js';
import { createExplainPanel } from './components/explainPanel.js';
import { createToastHost } from './components/toast.js';
import { createWarningsPanel } from './components/warningsPanel.js';

/** One-step imports have no review grid, so only a clean PDF may replace data. */
const MIN_AUTOMATIC_PDF_CONFIDENCE = 0.85;

/** Create the single-page upload-to-schedule experience. */
export function createApp({ store, actions }) {
  const disposers = [];
  let busy = false;
  let scheduleCache = null;
  let cancelScheduleEntrance = () => {};
  let demoCompletionFrame = null;

  const fileInput = el('input', {
    type: 'file',
    class: 'sr-only',
    accept: '.pdf,.csv,.tsv,.txt,application/pdf,text/csv,text/tab-separated-values,text/plain',
    attrs: { 'aria-label': 'Upload rota file' },
  });

  const uploadIcon = el('span', { class: 'simple-upload__icon' }, [icon('upload', { size: 28 })]);
  const uploadTitle = el('strong', { class: 'simple-upload__title', text: 'Drop your rota here' });
  const uploadText = el('span', { class: 'simple-upload__text', text: 'PDF, CSV, TSV or text - Lunchly does the rest' });
  const uploadButton = el('button', {
    type: 'button',
    class: 'simple-upload',
    on: { click: () => fileInput.click() },
  }, [uploadIcon, el('span', { class: 'simple-upload__copy' }, [uploadTitle, uploadText]), el('span', { class: 'simple-upload__cta', text: 'Choose rota' })]);

  const demoButton = el('button', {
    type: 'button',
    class: 'simple-demo-button',
    on: { click: () => {
      if (busy) return;
      busy = true;
      syncBusy();
      setPreviewState('working');
      actions.loadDemoWeek();
      actions.generate();
      demoCompletionFrame = window.requestAnimationFrame(() => {
        busy = false;
        demoCompletionFrame = null;
        syncBusy();
        setPreviewState('complete');
        actions.toast({ message: 'Demo rota scheduled - your lunch plan is ready.', tone: 'ok' });
        scrollToResults();
      });
    } },
  }, [icon('sparkles', { size: 15 }), el('span', { text: 'Try the demo rota' })]);

  const previewStatusText = el('span', { text: 'Coverage protected' });
  const schedulePreview = createSchedulePreview(previewStatusText);

  const heroStatus = el('p', { class: 'simple-hero__status', attrs: { 'aria-live': 'polite' } });
  // Decorative only, and entirely self-contained: see heroBackdrop.js.
  const heroBackdrop = createHeroBackdrop();
  const hero = el('header', { class: 'simple-hero' }, [
    heroBackdrop.element,
    el('div', { class: 'simple-hero__inner' }, [
      el('p', { class: 'simple-hero__credit', text: 'Created by Josh' }),
      el('div', { class: 'simple-hero__byline' }, [
        el('span', { class: 'simple-hero__logo' }, [el('img', { src: './company-logo.svg', alt: 'Hub NI', width: 154, height: 41 })]),
      ]),
      el('p', { class: 'simple-hero__eyebrow' }, [
        icon('clock', { size: 14 }),
        el('span', { text: 'Lunchly · automatic rota planning' }),
      ]),
      el('h1', { class: 'simple-hero__title' }, [
        el('span', { class: 'simple-hero__title-line', text: 'Smarter lunches.' }),
        el('span', { class: 'simple-hero__title-line simple-hero__title-accent', text: 'Better coverage.' }),
      ]),
      el('p', {
        class: 'simple-hero__intro',
        text: 'Turn your weekly rota into a balanced lunch schedule in seconds. Lunchly checks every shift and protects phone coverage automatically.',
      }),
      el('div', { class: 'simple-hero__actions' }, [
        uploadButton,
        fileInput,
        el('div', { class: 'simple-hero__secondary-actions' }, [
          demoButton,
          el('span', { class: 'simple-hero__formats', text: 'Works with PDF, CSV, TSV and text' }),
        ]),
      ]),
      heroStatus,
      el('div', { class: 'simple-hero__promise', attrs: { 'aria-label': 'Lunchly benefits' } }, [
        promise('check', 'Automatic'),
        promise('lock', 'Coverage-aware'),
        promise('clock', 'Ready in seconds'),
      ]),
      el('div', { class: 'simple-preview-shell' }, [schedulePreview]),
    ]),
  ]);

  const resultsHeading = el('h2', { class: 'simple-results__title', text: 'This week’s lunches' });
  const resultsSubtitle = el('p', { class: 'simple-results__subtitle' });
  const resultsGrid = el('div', { class: 'simple-results__grid' });
  const resultsSummary = el('div', { class: 'simple-results__summary' });
  const copyButton = el('button', { type: 'button', class: 'simple-action-button simple-action-button--primary', on: { click: copySchedule } }, [icon('copy', { size: 17 }), 'Copy full week']);
  const replaceButton = el('button', { type: 'button', class: 'simple-action-button', on: { click: () => fileInput.click() } }, [icon('upload', { size: 17 }), 'Upload another rota']);
  const warningsButton = el('button', { type: 'button', class: 'simple-action-button simple-action-button--quiet', on: { click: () => actions.openDialog('warnings') } }, [icon('info', { size: 17 }), el('span', { text: 'View notes' })]);
  const results = el('main', { class: 'simple-results', id: 'main-content', attrs: { tabindex: '-1' } }, [
    el('div', { class: 'simple-results__topline' }, [
      el('div', {}, [resultsHeading, resultsSubtitle]),
      el('div', { class: 'simple-results__actions' }, [copyButton, replaceButton, warningsButton]),
    ]),
    resultsSummary,
    resultsGrid,
  ]);

  const emptyPrompt = el('section', { class: 'simple-empty', attrs: { 'aria-label': 'How Lunchly works' } }, [
    el('p', { class: 'simple-empty__eyebrow', text: 'One upload. One clear plan.' }),
    el('div', { class: 'simple-empty__steps' }, [
      step('01', 'Upload', 'Choose the rota you already use.'),
      step('02', 'Lunchly plans', 'Every eligible shift gets a sensible slot.'),
      step('03', 'Share', 'Copy the finished plan straight to your team.'),
    ]),
  ]);

  const faq = el('section', { class: 'simple-faq', attrs: { 'aria-labelledby': 'lunchly-faq-title' } }, [
    el('div', { class: 'simple-faq__intro' }, [
      el('span', { class: 'simple-faq__icon', attrs: { 'aria-hidden': 'true' } }, [icon('help', { size: 24 })]),
      el('div', {}, [
        el('p', { class: 'simple-faq__eyebrow', text: 'Clear rules, not guesswork' }),
        el('h2', { id: 'lunchly-faq-title', class: 'simple-faq__title', text: 'What does Lunchly consider?' }),
        el('p', {
          class: 'simple-faq__lead',
          text: 'The short version of how Lunchly turns shifts into a fair, coverage-safe lunch plan.',
        }),
      ]),
    ]),
    el('div', { class: 'simple-faq__grid' }, [
      faqItem(
        'Who receives a lunch?',
        'Lunchly checks whether the person is working, their shift length, overnight status and any confirmed staff exception. Blank, absent and short-shift entries are not silently given a lunch.',
        true,
      ),
      faqItem(
        'How does it choose the time?',
        'It follows the confirmed shift ladder: 07:00 starts target 12:00, then later starts step through the day up to 13:00–21:00 targeting 16:00. The 14:00, 15:00, 16:00 and 17:00 starts then follow one another in the evening run. Ten-hour day shifts target 13:30–14:30, while confirmed personal rules still take priority.',
      ),
      faqItem(
        'How is phone coverage protected?',
        'Every proposed break is checked across its full duration. Lunchly will not choose a slot that drops the remaining team below the required number of people on duty.',
      ),
      faqItem(
        'What is the NISCC rule?',
        'Before 13:00 on weekdays, only one person may be at lunch at a time. From 13:00 onward, the normal daily sharing limit applies. Weekend profiles are handled separately.',
      ),
      faqItem(
        'Why are lunches usually back-to-back?',
        'Every daytime lunch run starts at 12:00 and then continues in consecutive 30-minute slots. On Saturdays, the separate later run starts at 18:30 and continues through about 20:00. A later start only appears when the opening placement is not legal or coverage-safe.',
      ),
      faqItem(
        'Do earlier starters eat earlier?',
        'Yes, where the hard rules allow it. Lunchly compares actual shift starts and keeps earlier starters ahead of later starters within the same compact lunch run.',
      ),
      faqItem(
        'When can a gap appear?',
        'Only when the next half-hour is unreasonable - for example because nobody is legally available then, the NISCC limit would be broken, or phone coverage would fall too low.',
      ),
      faqItem(
        'Does Lunchly copy last week’s answers?',
        'No. Previous lunch patterns are only a small hint. The current rota, confirmed names such as Cags being Caroline, and the live safety rules determine the new plan.',
      ),
    ]),
  ]);

  const warningsPanel = createWarningsPanel({ store, actions });
  const explainPanel = createExplainPanel({ store, actions });
  const toastHost = createToastHost({ store, actions });
  const element = el('div', { class: 'simple-shell' }, [
    hero,
    results,
    emptyPrompt,
    faq,
    el('footer', { class: 'simple-footer' }, [
      el('span', { text: 'Lunchly' }),
      el('span', { text: 'Built for the people who keep the phones moving.' }),
      // Which build is this browser actually running? Answering that from the
      // page itself is the quickest way to tell a real bug from a stale cache.
      el('span', {
        class: 'simple-footer__build',
        text: `Build ${typeof __LUNCHLY_BUILD__ === 'string' ? __LUNCHLY_BUILD__ : 'dev'}`,
        title: 'The version of Lunchly this browser has loaded',
      }),
    ]),
    warningsPanel.element,
    explainPanel.element,
    toastHost.element,
  ]);

  disposers.push(setupScrollReveals(element, [emptyPrompt, faq]));

  // Parallax is measured against the hero's box, so it can only be wired up once
  // the backdrop is inside it.
  heroBackdrop.observePointer(hero);
  disposers.push(() => heroBackdrop.destroy());

  disposers.push(on(fileInput, 'change', () => importFile(fileInput.files?.[0] ?? null)));
  for (const eventName of ['dragenter', 'dragover']) {
    disposers.push(on(uploadButton, eventName, (event) => {
      event.preventDefault();
      if (!busy) uploadButton.classList.add('is-dragging-over');
    }));
  }
  disposers.push(on(uploadButton, 'dragleave', () => uploadButton.classList.remove('is-dragging-over')));
  disposers.push(on(uploadButton, 'drop', (event) => {
    event.preventDefault();
    uploadButton.classList.remove('is-dragging-over');
    importFile(event.dataTransfer?.files?.[0] ?? null);
  }));

  async function importFile(file) {
    if (!file || busy) return;
    busy = true;
    syncBusy();
    setPreviewState('working');
    heroStatus.textContent = `Reading ${file.name}…`;

    try {
      const state = store.getState();
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '');
      const sourceText = isPdf ? '' : await file.text();
      const parsed = isPdf
        ? await extractRotaFromPdf(file, { weekCommencing: state.weekCommencing })
        : parsePastedRota(sourceText, { weekCommencing: state.weekCommencing });
      if (isPdf && (parsed?.confidence ?? 0) < MIN_AUTOMATIC_PDF_CONFIDENCE) {
        const percent = Math.round((parsed?.confidence ?? 0) * 100);
        throw new Error(
          `Lunchly could only read ${percent}% of that PDF confidently, so your saved rota was left unchanged.`,
        );
      }
      const rota = applyConfirmedRotaCorrections(parsed?.rota);
      if (!rota || isRotaEmpty(rota)) {
        const detail = parsed?.warnings?.[0]?.message ?? parsed?.warnings?.[0] ?? 'No readable rota rows were found.';
        throw new Error(detail);
      }

      const people = rota.rows.filter((row) => String(row.name ?? '').trim()).length;

      /*
       * File the rota under the week it says it is, not the week that happened
       * to be on screen. Uploading next week's rota and getting this week's
       * dates back is both wrong and quietly wrong — the plan looks right until
       * somebody reads the day headings.
       *
       * The week is taken from the document's own "WC 3 August 2026" heading or
       * its dated day columns, and reported in the status line so the change is
       * never silent.
       */
      const headerCells = parsed?.rawRows?.[0] ?? [];
      const detected = parsed?.detectedWeekCommencing
        ?? detectWeekCommencing(sourceText, headerCells, state.weekCommencing);
      const week = detected || rota.weekCommencing || state.weekCommencing;

      // A new upload replaces what came before: the old rota and its plan go, so
      // the results below can never be a mix of two different weeks.
      actions.replaceRota({ ...rota, weekCommencing: week });
      actions.generate();
      setPreviewState('complete');

      const weekLabel = formatWeekCommencing(week);
      heroStatus.textContent = `${file.name} uploaded · ${people} people scheduled · ${weekLabel}`;
      actions.toast({
        message: detected && detected !== state.weekCommencing
          ? `Done - ${people} people read. Dated ${weekLabel} from the file.`
          : `Done - ${people} people read and the lunch plan is ready.`,
        tone: 'ok',
        duration: 6500,
      });
      scrollToResults();
    } catch (error) {
      setPreviewState('idle');
      heroStatus.textContent = 'That file could not be turned into a rota.';
      actions.toast({ message: `Could not read that rota: ${error.message}`, tone: 'danger', duration: 9000 });
    } finally {
      busy = false;
      fileInput.value = '';
      syncBusy();
    }
  }

  function syncBusy() {
    uploadButton.disabled = busy;
    demoButton.disabled = busy;
    demoButton.setAttribute('aria-busy', String(busy));
    uploadButton.classList.toggle('is-busy', busy);
    uploadTitle.textContent = busy ? 'Building the lunch plan…' : 'Drop your rota here';
    uploadText.textContent = busy ? 'Checking shifts and protecting coverage' : 'PDF, CSV, TSV or text - Lunchly does the rest';
    replaceChildren(uploadIcon, [busy ? el('span', { class: 'spinner' }) : icon('upload', { size: 28 })]);
  }

  function setPreviewState(state) {
    const working = state === 'working';
    schedulePreview.classList.toggle('is-working', working);
    schedulePreview.classList.toggle('is-complete', state === 'complete');
    previewStatusText.textContent = working
      ? 'Checking every lunch slot'
      : (state === 'complete' ? 'Coverage protected' : 'Ready to protect coverage');
  }

  function scrollToResults() {
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
      ? 'auto'
      : 'smooth';
    window.setTimeout(() => results.scrollIntoView?.({ behavior, block: 'start' }), 60);
  }

  async function copySchedule() {
    const state = store.getState();
    const schedule = currentSchedule(state);
    if (!schedule) return;
    const deps = exportDeps(state);
    const text = formatLunchRotaWeek(schedule, deps, state.exportOptions);
    const html = formatLunchRotaWeekHtml(schedule, deps, state.exportOptions);
    const copied = await copyToClipboard(text, { html });
    actions.toast({
      message: copied ? 'Lunch plan copied - ready to paste into Slack.' : 'Clipboard access was blocked. Try again from a secure browser tab.',
      tone: copied ? 'ok' : 'warn',
    });
  }

  async function copyDaySchedule(dayKey) {
    const state = store.getState();
    const schedule = currentSchedule(state);
    if (!schedule) return;
    const deps = exportDeps(state);
    const copied = await copyToClipboard(
      formatLunchRotaDay(schedule, dayKey, deps),
      { html: formatLunchRotaDayHtml(schedule, dayKey, deps) },
    );
    actions.toast({
      message: copied
        ? `${DAY_LABELS[dayKey] ?? 'Day'} lunches copied - ready to paste into Slack.`
        : 'Clipboard access was blocked. Try again from a secure browser tab.',
      tone: copied ? 'ok' : 'warn',
    });
  }

  function exportDeps(state) {
    return {
      rota: currentRota(state),
      aliasBook: state.aliasBook,
      settings: state.settings,
      weekShifts: weekShifts(state),
    };
  }

  function renderSchedule(state, schedule) {
    const warnings = allWarnings(state);
    const assigned = schedule.stats?.assigned ?? DAY_KEYS.reduce((total, key) => total + (schedule.days[key]?.assignments?.length ?? 0), 0);
    resultsSubtitle.textContent = formatWeekCommencing(schedule.weekCommencing ?? state.weekCommencing);
    replaceChildren(resultsSummary, [
      summaryStat(String(assigned), 'lunches placed', 'pink', 0),
      summaryStat(String(schedule.stats?.eligible ?? assigned), 'eligible shifts', 'navy', 1),
      summaryStat(schedule.stats?.unassigned ? String(schedule.stats.unassigned) : 'All', schedule.stats?.unassigned ? 'need attention' : 'staff covered', schedule.stats?.unassigned ? 'amber' : 'green', 2),
    ]);
    warningsButton.hidden = warnings.length === 0;
    warningsButton.querySelector('span').textContent = `${warnings.length} ${warnings.length === 1 ? 'note' : 'notes'}`;

    replaceChildren(resultsGrid, DAY_KEYS.map((dayKey, index) => dayPlan(state, schedule, dayKey, index)));
    cancelScheduleEntrance();
    cancelScheduleEntrance = restartAnimationClass(results, 'is-schedule-entering');
  }

  function dayPlan(state, schedule, dayKey, dayIndex) {
    const day = schedule.days?.[dayKey];
    const assignments = [...(day?.assignments ?? [])].sort((a, b) => a.slotMinutes - b.slotMinutes);
    const slots = new Map();
    for (const assignment of assignments) {
      const list = slots.get(assignment.slotMinutes) ?? [];
      list.push(assignment);
      slots.set(assignment.slotMinutes, list);
    }
    const rows = [...slots.entries()].map(([minutes, people], slotIndex) => el('div', {
      class: 'simple-day__slot',
      style: { '--slot-index': slotIndex },
    }, [
      el('time', { class: 'simple-day__time', text: formatMinutes(minutes) }),
      el('div', { class: 'simple-day__people' }, people.map((assignment) => el('button', {
        type: 'button',
        class: ['simple-person', assignment.group && `simple-person--${assignment.group}`],
        attrs: { 'aria-label': `${displayName(state, assignment.employeeId)} at ${formatMinutes(minutes)}. Show explanation.` },
        on: { click: () => actions.openDialog('explain', { dayKey, employeeId: assignment.employeeId }) },
      }, [el('span', { class: 'simple-person__dot', attrs: { 'aria-hidden': 'true' } }), displayName(state, assignment.employeeId)]))),
    ]));
    const dayActions = el('div', { class: 'simple-day__actions' }, [
      assignments.length ? el('button', {
        type: 'button',
        class: 'simple-day__copy',
        attrs: {
          'aria-label': `Copy ${DAY_LABELS[dayKey] ?? dayKey} lunches`,
          title: `Copy ${DAY_LABELS[dayKey] ?? dayKey} lunches`,
        },
        on: { click: () => copyDaySchedule(dayKey) },
      }, [icon('copy', { size: 14 }), el('span', { text: 'Copy' })]) : null,
      el('span', { class: 'simple-day__count', text: String(assignments.length) }),
    ]);
    return el('article', {
      class: ['simple-day', assignments.length ? '' : 'simple-day--empty'],
      style: { '--day-index': dayIndex },
    }, [
      el('div', { class: 'simple-day__header' }, [
        el('div', {}, [el('p', { class: 'simple-day__weekday', text: DAY_LABELS[dayKey] }), el('h3', { class: 'simple-day__date', text: formatDayHeader(day?.date) })]),
        dayActions,
      ]),
      el('div', { class: 'simple-day__body' }, rows.length ? rows : [el('p', { class: 'simple-day__none', text: 'No lunches needed' })]),
    ]);
  }

  function update(state) {
    const schedule = currentSchedule(state);
    const ready = hasRota(state) && Boolean(schedule);
    results.hidden = !ready;
    emptyPrompt.hidden = ready;
    hero.classList.toggle('simple-hero--compact', ready);
    if (ready && schedule !== scheduleCache) {
      scheduleCache = schedule;
      renderSchedule(state, schedule);
    }
    warningsPanel.update(state);
    explainPanel.update(state);
    toastHost.update(state);
  }

  return {
    element,
    update,
    destroy() {
      if (demoCompletionFrame !== null) window.cancelAnimationFrame?.(demoCompletionFrame);
      cancelScheduleEntrance();
      disposers.forEach((dispose) => dispose());
      warningsPanel.destroy?.();
      explainPanel.destroy?.();
      toastHost.destroy?.();
    },
  };
}

function promise(iconName, label) {
  return el('span', {}, [icon(iconName, { size: 15 }), label]);
}

function step(number, title, description) {
  return el('article', { class: 'simple-step' }, [
    el('span', { class: 'simple-step__number', text: number }),
    el('h2', { class: 'simple-step__title', text: title }),
    el('p', { class: 'simple-step__text', text: description }),
  ]);
}

function faqItem(question, answer, open = false) {
  return el('details', { class: 'simple-faq__item', open }, [
    el('summary', { class: 'simple-faq__question', text: question }),
    el('p', { class: 'simple-faq__answer', text: answer }),
  ]);
}

function summaryStat(value, label, tone, index = 0) {
  return el('div', {
    class: ['simple-summary-stat', `simple-summary-stat--${tone}`],
    style: { '--summary-index': index },
  }, [
    el('strong', { text: value }),
    el('span', { text: label }),
  ]);
}

function createSchedulePreview(statusText) {
  return el('section', {
    class: 'simple-preview is-complete',
    attrs: {
      'aria-label': 'Example of Lunchly automatically assigning lunches while protecting coverage',
    },
  }, [
    el('div', { class: 'simple-preview__header' }, [
      el('div', { class: 'simple-preview__heading' }, [
        el('span', { class: 'simple-preview__mark' }, [icon('sparkles', { size: 15 })]),
        el('div', {}, [
          el('strong', { text: 'Automatic scheduling' }),
          el('span', { text: 'Monday · day team' }),
        ]),
      ]),
      el('span', { class: 'simple-preview__live' }, [
        el('span', { class: 'simple-preview__live-dot', attrs: { 'aria-hidden': 'true' } }),
        el('span', { text: 'Live example' }),
      ]),
    ]),
    el('div', { class: 'simple-preview__timeline', attrs: { 'aria-hidden': 'true' } }, [
      el('span', { text: '09:00' }),
      el('span', { text: '12:00' }),
      el('span', { text: '15:00' }),
      el('span', { text: '18:00' }),
    ]),
    el('div', { class: 'simple-preview__rows' }, [
      previewRow('jill', 'Parker', '07:30–15:30', '12:00'),
      previewRow('emma', 'Indigo', '08:00–16:00', '12:30'),
      previewRow('rachel', 'Xander', '09:00–21:00', '14:30'),
    ]),
    el('div', { class: 'simple-preview__coverage' }, [
      el('div', { class: 'simple-preview__coverage-copy' }, [
        el('span', { class: 'simple-preview__coverage-icon' }, [icon('lock', { size: 15 })]),
        el('div', {}, [
          el('strong', {}, [statusText]),
          el('span', { text: '8 staff remain available' }),
        ]),
      ]),
      el('div', {
        class: 'simple-preview__meter',
        attrs: { role: 'progressbar', 'aria-label': 'Example staff coverage', 'aria-valuemin': '0', 'aria-valuemax': '9', 'aria-valuenow': '8' },
      }, [el('span', { class: 'simple-preview__meter-fill' })]),
    ]),
  ]);
}

function previewRow(key, name, shift, lunch) {
  return el('div', { class: ['simple-preview__row', `simple-preview__row--${key}`] }, [
    el('div', { class: 'simple-preview__person' }, [
      el('span', { class: 'simple-preview__avatar', text: name.charAt(0), attrs: { 'aria-hidden': 'true' } }),
      el('div', {}, [el('strong', { text: name }), el('span', { text: shift })]),
    ]),
    el('div', { class: 'simple-preview__track' }, [
      el('span', { class: 'simple-preview__shift' }),
      el('span', { class: 'simple-preview__lunch', text: lunch }),
      el('span', { class: 'simple-preview__runner', attrs: { 'aria-hidden': 'true' } }),
    ]),
  ]);
}
